import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area,
} from "recharts";
import {
  Globe, TrendingUp, Bell, Search, Download, Send, Star, Bookmark,
  Check, X, Clock, ChevronRight, ChevronLeft, Calendar, MapPin, Award, Zap,
  FileText, Users, BarChart2, Target, Activity, AlertCircle, CheckCircle,
  Eye, Plus, RefreshCw, Mail, GraduationCap, Building2, Layers, Settings,
  LogOut, Home, Menu, Layout, Flag, Medal, Briefcase, ThumbsUp, Compass,
  PieChart as PieIcon, Filter, TrendingDown, ChevronDown,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB"); } catch { return d; }
};

const csvCell = (v) =>
  `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;

const downloadCSV = (filename, headers, rows) => {
  const csv = [headers.map(csvCell).join(","), ...rows.map((r) => r.map(csvCell).join(","))].join("\n");
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

// ─── Design Tokens ────────────────────────────────────────────────────────────
const T = {
  navy:      "#0a2352",
  navyDark:  "#061535",
  navyMid:   "#143272",
  navyLight: "#1e4a8a",
  orange:    "#f97316",
  orangeLight:"#fef0e7",
  bg:        "#f0f4f9",
  sidebar:   "#061535",
  card:      "#ffffff",
  border:    "#dde4ef",
  text:      "#000000",
  muted:     "#000000",
  light:     "#e8eef5",
  success:   "#16a34a",
  danger:    "#dc2626",
  warn:      "#d97706",
  purple:    "#7c3aed",
  accent:    "#2563eb",
  teal:      "#0d9488",
  pink:      "#db2777",
};
const FONT = "'DM Sans', 'Inter', 'Segoe UI', system-ui, sans-serif";
const DONUT_COLORS = [T.navy, T.accent, T.purple, T.teal, T.orange, T.pink, T.success];

// ─── Data helpers ─────────────────────────────────────────────────────────────
const monthOrder = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const countryFlag = {
  USA:"🇺🇸","United States":"🇺🇸",UK:"🇬🇧","United Kingdom":"🇬🇧",Canada:"🇨🇦",
  Germany:"🇩🇪",Australia:"🇦🇺",Singapore:"🇸🇬",India:"🇮🇳",France:"🇫🇷",
  Japan:"🇯🇵","S. Korea":"🇰🇷","South Korea":"🇰🇷","UAE":"🇦🇪","South Africa":"🇿🇦",
  Sweden:"🇸🇪",Brazil:"🇧🇷",Thailand:"🇹🇭",China:"🇨🇳",Switzerland:"🇨🇭"
};
const getFlag = (c) => countryFlag[c] || "🌐";
const toDate  = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d; };
const monthLabel = (v) => { const d = toDate(v); return d ? monthOrder[d.getMonth()] : null; };
const sparkFrom = (n) => Array.from({length:12},(_,i)=>Math.max(1,Math.round((Number(n)||1)*(0.6+i*0.06))));

// ─── Global state (module-level so renders are fast) ─────────────────────────
let D = {
  profile: null, events: [], applications: [], invitations: [],
  metrics: { applications:0, activeApplications:0, invitations:0, accepted:0, upcomingEvents:0, successRate:0 },
  trendData:[], regionData:[], countryData:[], appsTableData:[],
  invitationCards:[], recommendedEvents:[], kanbanData:{},
  donutRegionApps:[], donutRegionInv:[], donutCategory:[], donutAcceptance:[],
  timelineData:[], leaderboardCountries:[], leaderboardSchools:[], leaderboardCategories:[],
  notifications:[], activityFeed:[], profileMetrics:[],
  sparks: { events:sparkFrom(1), apps:sparkFrom(1), invites:sparkFrom(1), accept:sparkFrom(1), upcoming:sparkFrom(1), success:sparkFrom(1) },
};

const normalizeRow = (r) => ({
  ...r,
  country: r.country||"Unknown",
  school_name: r.school_name||r.school||r.entity_name||r.university_name||"Unknown",
});

const buildData = ({ profile, events=[], applications=[], invitations=[] }) => {
  const er = events.map(normalizeRow);
  const ar = applications.map((r) => ({
    ...normalizeRow(r),
    applied_on: r.applied_on||r.created_on||r.modified_date||null,
    invitation_status: r.invitation_status||"—",
    application_status: r.application_status||"applied",
    event_date: r.event_date||r.event_to_date||r.event_from_date||null,
  }));
  const ir = invitations.map((r) => ({
    ...normalizeRow(r),
    invited_on: r.invited_on||r.created_on||null,
    invitation_status: r.invitation_status||"pending",
    acceptance_deadline: r.acceptance_deadline||null,
  }));

  const metrics = {
    applications: ar.length,
    activeApplications: ar.filter(r=>!["rejected","withdrawn"].includes(r.application_status)).length,
    invitations: ir.length,
    accepted: ir.filter(r=>r.invitation_status==="accepted").length,
    upcomingEvents: er.filter(r=>["published","registration_open"].includes(r.event_status)).length,
    successRate: ar.length ? Math.round((ir.filter(r=>r.invitation_status==="accepted").length/ar.length)*100) : 0,
  };

  metrics.inviteRate =
  ar.length > 0
    ? Math.round((ir.length / ar.length) * 100)
    : 0;

metrics.acceptanceRate =
  ir.length > 0
    ? Math.round(
        (ir.filter(x => x.invitation_status === "accepted").length /
          ir.length) *
          100
      )
    : 0;

metrics.eventsAttended =
  ir.filter(x => x.invitation_status === "accepted").length;

metrics.avgResponseDays = (() => {
  const responded = ir.filter(
    x => x.invited_on && x.responded_on
  );

  if (!responded.length) return 0;

  const totalDays = responded.reduce((sum, r) => {
    const invited = new Date(r.invited_on);
    const respondedDate = new Date(r.responded_on);

    return (
      sum +
      (respondedDate - invited) /
        (1000 * 60 * 60 * 24)
    );
  }, 0);

  return Number(
    (totalDays / responded.length).toFixed(1)
  );
})();

  const trendData = monthOrder.map(month=>({
    month,
    applied:  ar.filter(r=>monthLabel(r.applied_on)===month).length,
    invited:  ir.filter(r=>monthLabel(r.invited_on)===month).length,
    accepted: ir.filter(r=>monthLabel(r.invited_on)===month&&r.invitation_status==="accepted").length,
  })).filter(r=>r.applied||r.invited||r.accepted);

  const appStatuses = ar.reduce((a,r)=>{a[r.application_status]=(a[r.application_status]||0)+1;return a},{});
  const invStatuses = ir.reduce((a,r)=>{a[r.invitation_status]=(a[r.invitation_status]||0)+1;return a},{});

  const countriesRaw = ar.reduce((a,r)=>{
    const k=r.country||"Unknown";
    a[k]=a[k]||{country:k,applications:0,invitations:0,attended:0};
    a[k].applications++;
    a[k].invitations+=ir.filter(i=>i.country===k).length;
    a[k].attended+=ir.filter(i=>i.country===k&&i.invitation_status==="accepted").length;
    return a;
  },{});
  const topCountries = Object.values(countriesRaw).map(r=>({
    ...r, flag:getFlag(r.country),
    pct: r.applications ? Math.round((r.attended/r.applications)*100) : 0,
  })).sort((a,b)=>b.applications-a.applications);

  const regionData = topCountries.map(r=>({region:r.country,applied:r.applications,invited:r.invitations,attended:r.attended,flag:r.flag}));
  const appsTableData = ar.map(r=>{
    const ev=er.find(e=>e.primary_id===r.event_id)||{};
    return {
      id:r.primary_id||r.id||r.event_id,
      event:r.event_name||ev.event_name||"Unknown Event",
      school:r.school_name||profile?.university_name||"Unknown School",
      country:r.country||"Unknown",
      applied:fmtDate(r.applied_on||r.created_on||r.modified_date),
      status:r.application_status||"applied",
      invStatus:r.invitation_status||"—",
      eventDate:fmtDate(r.event_date||ev.event_date||ev.event_to_date||ev.event_from_date),
    };
  });

  const invitationCards = ir.map(r=>{
    const daysLeft=r.acceptance_deadline?Math.max(0,Math.ceil((toDate(r.acceptance_deadline)-new Date())/(1000*60*60*24))):null;
    return {
      id:r.primary_id||r.id||r.event_id,
      school:r.school_name||profile?.university_name||"Your University",
      country:`${r.country||"Unknown"} ${getFlag(r.country)}`,
      event:r.event_name||"Event Invitation",
      deadline:fmtDate(r.acceptance_deadline||r.invited_on),
      priority:daysLeft!=null&&daysLeft<=7?"high":daysLeft!=null&&daysLeft<=21?"medium":"low",
      status:r.invitation_status||"pending",
      daysLeft,
    };
  });

  const recSrc = er.filter(r=>["published","registration_open"].includes(r.event_status));
  const recommendedEvents = recSrc.map((ev,i)=>({
    id:ev.primary_id||i+1,
    name:ev.event_name,
    country:`${ev.country||"Unknown"} ${getFlag(ev.country)}`,
    school:profile?.university_name||"Your University",
    match:Math.max(72,98-i*2),
    category:ev.event_category||"General",
    deadline:fmtDate(ev.reg_close_date||ev.event_to_date||ev.event_from_date),
    saved:i%2===0,
  })).slice(0,6);

  const kanbanData = {
    Discovered: recSrc.slice(0,3).map((ev,i)=>({id:ev.primary_id||i,event:ev.event_name,country:`${ev.country||"Unknown"} ${getFlag(ev.country)}`,cat:ev.event_category||"General"})),
    Applied:    ar.filter(r=>r.application_status==="applied").slice(0,3).map((r,i)=>({id:r.primary_id||i,event:r.event_name,country:`${r.country||"Unknown"} ${getFlag(r.country)}`,cat:"Applied"})),
    "Under Review": ar.filter(r=>r.application_status==="under_review").slice(0,3).map((r,i)=>({id:r.primary_id||i,event:r.event_name,country:`${r.country||"Unknown"} ${getFlag(r.country)}`,cat:"Review"})),
    Invited:    ir.filter(r=>r.invitation_status==="pending").slice(0,3).map((r,i)=>({id:r.primary_id||i,event:r.event_name,country:`${r.country||"Unknown"} ${getFlag(r.country)}`,cat:"Invite"})),
    Confirmed:  ir.filter(r=>r.invitation_status==="accepted").slice(0,3).map((r,i)=>({id:r.primary_id||i,event:r.event_name,country:`${r.country||"Unknown"} ${getFlag(r.country)}`,cat:"Confirmed"})),
    Completed:  er.filter(r=>r.event_status==="completed").slice(0,3).map((r,i)=>({id:r.primary_id||i,event:r.event_name,country:`${r.country||"Unknown"} ${getFlag(r.country)}`,cat:"Completed"})),
  };

  const donutRegionApps = topCountries.slice(0,6).map(r=>({name:r.country,value:r.applications||1}));
  const donutRegionInv  = topCountries.slice(0,6).map(r=>({name:r.country,value:r.invitations||1}));
  const donutCategory   = Object.entries(er.reduce((a,r)=>{const k=r.event_category||"General";a[k]=(a[k]||0)+1;return a},{})).map(([name,value])=>({name,value}));
  const donutAcceptance = [
    {name:"Accepted",value:invStatuses.accepted||1},
    {name:"Pending",value:invStatuses.pending||1},
    {name:"Declined",value:invStatuses.declined||1},
    {name:"Expired",value:invStatuses.expired||1},
  ];

  const timelineData = [
    ...ar.map(r=>({date:fmtDate(r.applied_on||r.created_on),type:"application",label:"Application Submitted",detail:`${r.event_name} · ${r.country||"Unknown"}`,urgent:false})),
    ...ir.map(r=>({date:fmtDate(r.invited_on||r.acceptance_deadline),type:r.invitation_status==="accepted"?"accepted":"invitation",label:r.invitation_status==="accepted"?"Invitation Accepted":"Invitation Received",detail:`${r.event_name} · ${r.country||"Unknown"}`,urgent:r.invitation_status==="pending"})),
  ].sort((a,b)=>(toDate(a.date)?.getTime()||0)-(toDate(b.date)?.getTime()||0)).slice(0,10);

  const leaderboardCountries = topCountries.slice(0,5).map((r,i)=>({rank:i+1,name:r.country,flag:r.flag,count:r.applications,pct:r.pct,color:[T.success,T.accent,T.teal,T.purple,T.orange][i%5]}));
  const leaderboardSchools = Object.entries(ar.reduce((a,r)=>{const k=r.school_name||profile?.university_name||"Your University";a[k]=a[k]||{count:0,country:r.country||"Unknown"};a[k].count++;return a},{})).map(([name,v],i)=>({rank:i+1,name,count:v.count,type:v.country,color:[T.success,T.accent,T.teal,T.purple,T.orange][i%5]})).slice(0,5);
  const leaderboardCategories = Object.entries(er.reduce((a,r)=>{const k=r.event_category||"General";a[k]=(a[k]||0)+1;return a},{})).map(([name,count],i)=>({rank:i+1,name,count,pct:ar.length?Math.round((count/ar.length)*100):0,color:[T.navy,T.accent,T.purple,T.teal,T.orange][i%5]})).slice(0,5);

  const notifications = [
    ...ir.slice(0,3).map(r=>({id:r.primary_id,icon:Mail,color:T.purple,title:r.invitation_status==="accepted"?"Invitation Accepted":"Invitation Received",sub:`${r.event_name} · ${r.country||"Unknown"}`,time:r.invited_on?fmtDate(r.invited_on):"Recently",unread:r.invitation_status!=="accepted"})),
    ...ar.slice(0,3).map(r=>({id:r.primary_id,icon:FileText,color:T.accent,title:"Application Update",sub:`${r.event_name} · ${r.country||"Unknown"}`,time:r.applied_on?fmtDate(r.applied_on):"Recently",unread:true})),
  ].slice(0,6);

  const activityFeed = [
    ...ar.slice(0,6).map(r=>({action:r.application_status==="shortlisted"?"Application shortlisted":"Application submitted",detail:`${r.event_name} · ${r.country||"Unknown"}`,time:r.applied_on?fmtDate(r.applied_on):"Recently",type:"applied"})),
    ...ir.slice(0,6).map(r=>({action:r.invitation_status==="accepted"?"Invitation accepted":"Invitation received",detail:`${r.event_name} · ${r.country||"Unknown"}`,time:r.invited_on?fmtDate(r.invited_on):"Recently",type:r.invitation_status})),
  ].slice(0,12);

  const profileRow = profile||{};
  const fields = ["university_name","city","country","email","university_type","rep1_name","rep1_contact","rep1_email"];
  const filled  = fields.filter(f=>profileRow[f]).length;
  const profileCompletion = Math.round((filled/fields.length)*100);
  const acceptedRate = ar.length ? Math.round((invStatuses.accepted||0)/ar.length*100) : 0;
  const profileMetrics = [
    {label:"Profile Completion",value:profileCompletion,color:T.navy,icon:Target},
    {label:"Response Rate",value:Math.min(99,Math.max(45,100-(invStatuses.pending||0)*5)),color:T.success,icon:Clock},
    {label:"Engagement Score",value:Math.min(99,Math.max(40,metrics.successRate||0)),color:T.accent,icon:ThumbsUp},
    {label:"Invitation Success",value:Math.min(99,Math.max(40,acceptedRate||0)),color:T.purple,icon:Award},
    {label:"Global Percentile",value:Math.min(99,Math.max(30,topCountries.length?100-topCountries.findIndex(r=>r.country===(profileRow.country||""))*5:71)),color:T.teal,icon:Globe},
    {label:"Quality Score",value:Math.min(99,Math.max(50,Math.round((profileCompletion*0.6)+(metrics.successRate*0.4)))),color:T.orange,icon:Medal},
  ];


  return { profile:profileRow, events:er, applications:ar, invitations:ir, metrics, trendData, regionData, countryData:topCountries, appsTableData, invitationCards, recommendedEvents, kanbanData, donutRegionApps, donutRegionInv, donutCategory, donutAcceptance, timelineData, leaderboardCountries, leaderboardSchools, leaderboardCategories, notifications, activityFeed, profileMetrics,
    sparks:{events:sparkFrom(metrics.applications||1),apps:sparkFrom(metrics.activeApplications||1),invites:sparkFrom(metrics.invitations||1),accept:sparkFrom(metrics.accepted||1),upcoming:sparkFrom(metrics.upcomingEvents||1),success:sparkFrom(metrics.successRate||1)},
  };
};

// ─── Primitives ───────────────────────────────────────────────────────────────
const Sparkline = ({data,color,width=64,height=24}) => {
  const mn=Math.min(...data), mx=Math.max(...data);
  const pts=data.map((v,i)=>{const x=(i/(data.length-1))*width;const y=height-((v-mn)/(mx-mn||1))*(height-4)-2;return `${x},${y}`;}).join(" ");
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none"><path d={`M${pts.replace(/ /g," L")} L${width},${height} L0,${height} Z`} fill={color} fillOpacity=".15"/><polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
};

const RadialProgress = ({value,color,size=64,strokeWidth=6}) => {
  const r=(size-strokeWidth*2)/2, circ=2*Math.PI*r, dash=(value/100)*circ;
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}><circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.light} strokeWidth={strokeWidth}/><circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circ-dash}`} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}/><text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle" fontSize={size*.18} fontWeight="800" fill={color}>{value}%</text></svg>;
};

const Badge = ({status}) => {
  const MAP = {applied:{label:"Applied",bg:"#dbeafe",color:"#1d4ed8"},under_review:{label:"Review",bg:"#fef3c7",color:"#92400e"},shortlisted:{label:"Shortlisted",bg:"#d1fae5",color:"#065f46"},rejected:{label:"Rejected",bg:"#fee2e2",color:"#991b1b"},pending:{label:"Pending",bg:"#fef3c7",color:"#92400e"},accepted:{label:"Accepted",bg:"#d1fae5",color:"#065f46"},declined:{label:"Declined",bg:"#fee2e2",color:"#991b1b"},invited:{label:"Invited",bg:"#ede9fe",color:"#5b21b6"},expired:{label:"Expired",bg:"#f3f4f6",color:"#6b7280"},withdrawn:{label:"Withdrawn",bg:"#f3f4f6",color:"#6b7280"},"—":{label:"—",bg:"#f3f4f6",color:"#6b7280"}};
  const m=MAP[status]||{label:status,bg:"#f3f4f6",color:"#6b7280"};
  return <span style={{background:m.bg,color:m.color,padding:"2px 8px",borderRadius:99,fontSize:10,fontWeight:800,letterSpacing:.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{m.label}</span>;
};

const ProgressBar = ({value,color,height=5}) => (
  <div style={{height,background:T.light,borderRadius:99,overflow:"hidden"}}>
    <div style={{height:"100%",width:`${Math.min(100,value||0)}%`,background:color,borderRadius:99,transition:"width .4s ease"}}/>
  </div>
);

const DonutTooltip = ({active,payload}) => {
  if (!active||!payload?.length) return null;
  return <div style={{background:"#fff",border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",fontSize:11,boxShadow:"0 4px 16px rgba(0,0,0,.1)"}}><div style={{fontWeight:700,color:T.navy}}>{payload[0].name}</div><div style={{color:T.muted}}>{payload[0].value}</div></div>;
};

// ─── Panel shell (dense card) ─────────────────────────────────────────────────
const Panel = ({children,style={}}) => (
  <div style={{background:T.card,borderRadius:10,border:`1px solid ${T.border}`,boxShadow:"0 1px 8px rgba(10,35,82,.06)",overflow:"hidden",fontFamily:FONT,...style}}>
    {children}
  </div>
);

const PanelHeader = ({icon:Icon,title,right}) => (
  <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:"#fafbfd"}}>
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <Icon size={14} color={T.navy} strokeWidth={2.5}/>
      <span style={{fontSize:12,fontWeight:800,color:T.navy,letterSpacing:.2,textTransform:"uppercase"}}>{title}</span>
    </div>
    {right && <div style={{display:"flex",alignItems:"center",gap:6}}>{right}</div>}
  </div>
);

// ─── NAV CONFIG ──────────────────────────────────────────────────────────────
const NAV = [
  {key:"overview",   label:"Overview",     icon:Home},
  {key:"applications",label:"Applications",icon:FileText},
  {key:"invitations",label:"Invitations",  icon:Mail},
  {key:"events",     label:"Events",       icon:Calendar},
  {key:"analytics",  label:"Analytics",    icon:BarChart2},
  {key:"pipeline",   label:"Pipeline",     icon:Layers},
  {key:"insights",   label:"Insights",     icon:Medal},
];

// ═════════════════════════════════════════════════════════════════════════════
// TAB VIEWS
// ═════════════════════════════════════════════════════════════════════════════

// ── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({data}) {
  const {
    metrics, sparks, trendData, countryData,
    activityFeed, notifications, applications,
    invitations, events, recommendedEvents
  } = data;

  const [feedTab, setFeedTab] = useState("notifications");

  const KPIs = [
    {label:"Events Applied",     value:metrics.applications,      color:T.navy,   spark:sparks.events,   icon:FileText},
    {label:"Active Applications",value:metrics.activeApplications,color:T.accent, spark:sparks.apps,     icon:Layers},
    {label:"Invitations",        value:metrics.invitations,       color:T.purple, spark:sparks.invites,  icon:Mail},
    {label:"Accepted",           value:metrics.accepted,          color:T.success,spark:sparks.accept,   icon:CheckCircle},
    {label:"Upcoming Events",    value:metrics.upcomingEvents,    color:T.teal,   spark:sparks.upcoming, icon:Calendar},
    {label:"Success Rate",       value:`${metrics.successRate}%`, color:T.orange, spark:sparks.success,  icon:TrendingUp},
  ];

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gridTemplateRows:"auto",gap:14}}>
      {/* KPI row */}
      {KPIs.map((k,i) => (
        <Panel key={i} style={{gridColumn:"span 2",padding:"14px 16px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div style={{width:30,height:30,background:k.color+"15",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <k.icon size={14} color={k.color}/>
            </div>
            <Sparkline data={k.spark} color={k.color}/>
          </div>
          <div style={{fontSize:26,fontWeight:900,color:T.navy,letterSpacing:-1,lineHeight:1}}>{k.value}</div>
          <div style={{fontSize:15,color:T.muted,fontWeight:700,marginTop:4,textTransform:"uppercase",letterSpacing:.4}}>{k.label}</div>
        </Panel>
      ))}

      <Panel style={{ gridColumn: "span 4" }}>
  <PanelHeader
    icon={Award}
    title="University Performance"
  />

  <div
    style={{
      padding: "14px",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
    }}
  >
    {[
      {
        label: "Invite Rate",
        value: `${metrics.inviteRate}%`,
        icon: Mail,
        color: T.purple,
      },
      {
        label: "Acceptance Rate",
        value: `${metrics.acceptanceRate}%`,
        icon: CheckCircle,
        color: T.success,
      },
      {
        label: "Events Attended",
        value: metrics.eventsAttended,
        icon: Calendar,
        color: T.teal,
      },
      {
        label: "Avg Response Time",
        value: `${metrics.avgResponseDays}d`,
        icon: Clock,
        color: T.orange,
      },
    ].map((item, i) => (
      <div
        key={i}
        style={{
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          padding: "12px",
          background: "#fff",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: item.color + "15",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 10,
          }}
        >
          <item.icon
            size={15}
            color={item.color}
          />
        </div>

        <div
          style={{
            fontSize: 24,
            fontWeight: 900,
            color: item.color,
            lineHeight: 1,
          }}
        >
          {item.value}
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 10,
            fontWeight: 700,
            color: T.muted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {item.label}
        </div>
      </div>
    ))}
  </div>
</Panel>


<Panel style={{ gridColumn: "span 4" }}>
  <div
    style={{
      display: "flex",
      borderBottom: `1px solid ${T.border}`,
      background: "#fafbfd",
    }}
  >
    <button
      onClick={() => setFeedTab("notifications")}
      style={{
        flex: 1,
        padding: "12px",
        border: "none",
        cursor: "pointer",
        fontWeight: 800,
        fontSize: 11,
        background:
          feedTab === "notifications"
            ? "#fff"
            : "transparent",
        color:
          feedTab === "notifications"
            ? T.navy
            : T.muted,
        borderBottom:
          feedTab === "notifications"
            ? `2px solid ${T.accent}`
            : "none",
      }}
    >
      Notifications
    </button>

    <button
      onClick={() => setFeedTab("activity")}
      style={{
        flex: 1,
        padding: "12px",
        border: "none",
        cursor: "pointer",
        fontWeight: 800,
        fontSize: 11,
        background:
          feedTab === "activity"
            ? "#fff"
            : "transparent",
        color:
          feedTab === "activity"
            ? T.navy
            : T.muted,
        borderBottom:
          feedTab === "activity"
            ? `2px solid ${T.accent}`
            : "none",
      }}
    >
      Recent Activity
    </button>
  </div>

  <div
    style={{
      overflowY: "auto",
    }}
  >
    {feedTab === "notifications" &&
      notifications.map((n, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 10,
            padding: "12px 16px",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: n.color + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <n.icon size={12} color={n.color} />
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: T.navy,
              }}
            >
              {n.title}
            </div>

            <div
              style={{
                fontSize: 10,
                color: T.muted,
                marginTop: 2,
              }}
            >
              {n.sub}
            </div>
          </div>
        </div>
      ))}

    {feedTab === "activity" &&
      activityFeed.map((a, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 10,
            padding: "12px 16px",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              marginTop: 5,
              background:
                {
                  applied: T.accent,
                  invited: T.purple,
                  accepted: T.success,
                }[a.type] || T.muted,
            }}
          />

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: T.navy,
              }}
            >
              {a.action}
            </div>

            <div
              style={{
                fontSize: 10,
                color: T.muted,
                marginTop: 2,
              }}
            >
              {a.detail}
            </div>
          </div>

          <div
            style={{
              fontSize: 9,
              color: T.muted,
            }}
          >
            {a.time}
          </div>
        </div>
      ))}
  </div>
</Panel>
      {/* Activity feed filler */}
      <Panel style={{gridColumn:"span 4"}}>
        <PanelHeader icon={Activity} title="Quick Stats"/>
        <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:12}}>
          {[
            {label:"Acceptance Rate", value:`${metrics.successRate}%`, color:T.success},
            {label:"Active Applications", value:metrics.activeApplications, color:T.accent},
            {label:"Pending Invitations", value:invitations.filter(i=>i.invitation_status==="pending").length, color:T.warn},
            {label:"Upcoming Events", value:metrics.upcomingEvents, color:T.teal},
          ].map((s,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:s.color+"08",borderRadius:8,border:`1px solid ${s.color}22`}}>
              <span style={{fontSize:15,fontWeight:600,color:T.text}}>{s.label}</span>
              <span style={{fontSize:20,fontWeight:900,color:s.color}}>{s.value}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ── Applications Tab ──────────────────────────────────────────────────────────
function ApplicationsTab({data}) {
  // ✅ Added countryData and metrics to destructure
  const {
  appsTableData,
  profileMetrics,
  countryData,
  metrics
} = data;
  const [search,setSearch] = useState("");
  const [statusFilter,setStatusFilter] = useState("");
  const [page,setPage] = useState(1);
  const PER_PAGE = 10;

  const filtered = appsTableData.filter(r=>
    (!search||r.event.toLowerCase().includes(search.toLowerCase())||r.country.toLowerCase().includes(search.toLowerCase()))&&
    (!statusFilter||r.status===statusFilter)
  );
  const totalPages = Math.max(1,Math.ceil(filtered.length/PER_PAGE));
  const paged = filtered.slice((page-1)*PER_PAGE,page*PER_PAGE);

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:14}}>

      {/* ── Row 1: Table (span 8) + Right column (span 4) ── */}

      {/* Table */}
      <Panel style={{gridColumn:"span 8"}}>
        <PanelHeader icon={FileText} title="All Applications" right={
          <>
            <div style={{position:"relative"}}>
              <Search size={11} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:T.muted}}/>
              <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Search…"
                style={{padding:"5px 10px 5px 26px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:11,color:T.text,background:"#fff",outline:"none",width:160}}/>
            </div>
            <select value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}
              style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:11,color:T.text,background:"#fff",outline:"none"}}>
              <option value="">All Statuses</option>
              {["applied","under_review","shortlisted","rejected"].map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
            </select>
            <button onClick={()=>downloadCSV("applications.csv",["Event","School","Country","Applied","Status","Invitation","Event Date"],filtered.map(r=>[r.event,r.school,r.country,r.applied,r.status,r.invStatus,r.eventDate]))}
              style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:"#fff",fontSize:11,fontWeight:700,color:T.navy,cursor:"pointer"}}>
              <Download size={11}/> Export
            </button>
          </>
        }/>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:`linear-gradient(90deg,${T.navy},${T.navyMid})`}}>
                {["Event Name","Country","Applied Date","App Status","Invitation","Event Date"].map(h=>(
                  <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,fontWeight:800,color:"rgba(255,255,255,.8)",textTransform:"uppercase",letterSpacing:.4,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length===0?(
                <tr><td colSpan={6} style={{padding:"28px 12px",textAlign:"center",fontSize:12,color:T.muted}}>No applications found.</td></tr>
              ):paged.map((r,i)=>(
                <tr key={r.id} style={{borderBottom:`1px solid ${T.border}`,background:i%2===0?"#fff":T.bg+"88",cursor:"pointer"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.light}
                    onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":T.bg+"88"}>
                  <td style={{padding:"10px 12px",fontSize:12,fontWeight:700,color:T.navy,maxWidth:220}}><div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.event}</div></td>
                  <td style={{padding:"10px 12px",fontSize:11,color:T.muted,whiteSpace:"nowrap"}}>{r.country}</td>
                  <td style={{padding:"10px 12px",fontSize:11,color:T.muted,whiteSpace:"nowrap"}}>{r.applied}</td>
                  <td style={{padding:"10px 12px"}}><Badge status={r.status}/></td>
                  <td style={{padding:"10px 12px"}}><Badge status={r.invStatus}/></td>
                  <td style={{padding:"10px 12px",fontSize:11,color:T.muted,whiteSpace:"nowrap"}}>{r.eventDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 16px",borderTop:`1px solid ${T.border}`,fontSize:11,color:T.muted,background:"#fafbfd"}}>
          <span>Showing {filtered.length===0?0:Math.min((page-1)*PER_PAGE+1,filtered.length)}–{Math.min(page*PER_PAGE,filtered.length)} of {filtered.length}</span>
          <div style={{display:"flex",gap:4}}>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
              style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${T.border}`,background:"#fff",cursor:page===1?"not-allowed":"pointer",opacity:page===1?.5:1,fontSize:11}}>‹</button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>i+1).map(p=>(
              <button key={p} onClick={()=>setPage(p)}
                style={{width:24,height:24,borderRadius:5,border:`1px solid ${page===p?T.navy:T.border}`,background:page===p?T.navy:"#fff",color:page===p?"#fff":T.text,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FONT}}>{p}</button>
            ))}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${T.border}`,background:"#fff",cursor:page===totalPages?"not-allowed":"pointer",opacity:page===totalPages?.5:1,fontSize:11}}>›</button>
          </div>
        </div>
      </Panel>

      {/* Right column — Funnel + Profile Scores */}
      <div style={{gridColumn:"span 4",display:"flex",flexDirection:"column",gap:14}}>

        {/* Profile Scores */}
        <Panel>
          <PanelHeader icon={Award} title="Profile Scores"/>
          <div style={{padding:"12px 14px"}}>
            {profileMetrics.map((m,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <RadialProgress value={m.value} color={m.color} size={40} strokeWidth={4}/>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:T.navy,lineHeight:1.3}}>{m.label}</div>
                  <div style={{fontSize:9,color:T.muted,marginTop:2}}>{m.value>=85?"Excellent":m.value>=70?"Good":"Needs work"}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* ── Row 2: Strategic Snapshot — full width ── */}
      <Panel style={{gridColumn:"span 12"}}>
        <PanelHeader icon={Compass} title="Strategic Snapshot"/>
        <div style={{
          padding:"14px 16px",
          display:"grid",
          gridTemplateColumns:"repeat(6,1fr)",
          gap:12,
        }}>
          {[
            {
              label:"Schools Connected",
              value: new Set(data.applications.map(x=>x.school_name).filter(Boolean)).size,
              icon:Building2,
              color:T.navy,
            },
            {
              label:"Countries Active",
              value: countryData.length,
              icon:Globe,
              color:T.teal,
            },
            {
              label:"Pending Invites",
              value: data.invitations.filter(x=>x.invitation_status==="pending").length,
              icon:Mail,
              color:T.purple,
            },
            {
              label:"Upcoming Events",
              value: metrics.upcomingEvents,
              icon:Calendar,
              color:T.orange,
            },
            {
              label:"Categories Joined",
              value: new Set(data.events.map(x=>x.event_category).filter(Boolean)).size,
              icon:Flag,
              color:T.accent,
            },
            {
              label:"Saved Pipeline",
              value: data.recommendedEvents.filter(x=>x.saved).length,
              icon:Bookmark,
              color:T.success,
            },
          ].map((item,i)=>(
            <div key={i} style={{
              border:`1px solid ${T.border}`,
              borderRadius:10,
              padding:"16px 14px",
              background:"#fff",
              display:"flex",
              flexDirection:"column",
              gap:8,
            }}>
              <div style={{
                width:34,
                height:34,
                borderRadius:9,
                background:item.color+"15",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
              }}>
                <item.icon size={16} color={item.color}/>
              </div>
              <div style={{fontSize:26,fontWeight:900,color:item.color,lineHeight:1,letterSpacing:-1}}>
                {item.value}
              </div>
              <div style={{
                fontSize:10,
                fontWeight:700,
                color:T.muted,
                textTransform:"uppercase",
                letterSpacing:0.4,
                lineHeight:1.3,
              }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </Panel>

    </div>
  );
}

// ── Invitations Tab ───────────────────────────────────────────────────────────
function InvitationsTab({data}) {
  const {invitationCards} = data;
  const [filter,setFilter] = useState("all");

  const counts = {all:invitationCards.length,pending:invitationCards.filter(i=>i.status==="pending").length,accepted:invitationCards.filter(i=>i.status==="accepted").length,declined:invitationCards.filter(i=>i.status==="declined").length};
  const filtered = filter==="all" ? invitationCards : invitationCards.filter(i=>i.status===filter);
  const priorityColor = {high:T.danger,medium:T.warn,low:T.success};

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:14}}>
      {/* Status bar */}
      {[
        {key:"all",   label:"All",      value:counts.all,     color:T.navy},
        {key:"pending",label:"Pending", value:counts.pending, color:T.warn},
        {key:"accepted",label:"Accepted",value:counts.accepted,color:T.success},
        {key:"declined",label:"Declined",value:counts.declined,color:T.danger},
      ].map(s=>(
        <Panel key={s.key} style={{gridColumn:"span 3",padding:"14px 18px",cursor:"pointer",border:`1.5px solid ${filter===s.key?s.color:T.border}`,transition:"all .15s"}} onClick={()=>setFilter(s.key)}>
          <div style={{fontSize:24,fontWeight:900,color:s.color,letterSpacing:-1}}>{s.value}</div>
          <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:.5,marginTop:3}}>{s.label}</div>
        </Panel>
      ))}

      {/* Cards grid */}
      <Panel style={{gridColumn:"span 12"}}>
        <PanelHeader icon={Mail} title="Invitation Cards"/>
        <div style={{padding:16}}>
          {filtered.length===0 ? (
            <div style={{textAlign:"center",padding:"40px 0",color:T.muted,fontSize:13}}>No invitations in this category.</div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
              {filtered.map((inv,i)=>(
                <div key={i} style={{border:`1px solid ${T.border}`,borderRadius:10,padding:14,background:"#fff",borderLeft:`3px solid ${priorityColor[inv.priority]||T.muted}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <div style={{fontSize:12,fontWeight:800,color:T.navy,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginRight:8}}>{inv.school}</div>
                    <Badge status={inv.status}/>
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:4}}>{inv.country}</div>
                  <div style={{fontSize:11,color:T.text,fontWeight:600,marginBottom:10,lineHeight:1.35}}>{inv.event}</div>
                  <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:10}}>
                    <Clock size={10} color={T.muted}/>
                    <span style={{fontSize:10,color:inv.daysLeft&&inv.daysLeft<7?T.danger:T.muted}}>
                      {inv.daysLeft?`${inv.deadline} · ${inv.daysLeft}d left`:"Responded"}
                    </span>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    {inv.status==="pending"&&<>
                      <button style={{flex:1,padding:"5px 0",borderRadius:6,border:"none",background:T.success,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FONT}}>Accept</button>
                      <button style={{flex:1,padding:"5px 0",borderRadius:6,border:`1px solid ${T.border}`,background:"#fff",color:T.muted,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FONT}}>Decline</button>
                    </>}
                    {inv.status!=="pending"&&<button style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${T.border}`,background:"#fff",color:T.navy,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FONT}}>View Details</button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

// ── Events Tab ────────────────────────────────────────────────────────────────
function EventsTab({data}) {
  const {recommendedEvents,timelineData,kanbanData:kb} = data;
  const [saved,setSaved] = useState(()=>recommendedEvents.reduce((a,e)=>({...a,[e.id]:e.saved}),{}));

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:14}}>
      {/* Recommended */}
      <Panel style={{gridColumn:"span 7"}}>
        <PanelHeader icon={Star} title="Recommended Events" right={
          <button style={{fontSize:11,color:T.accent,border:"none",background:"none",cursor:"pointer",fontWeight:700}}>Refresh</button>
        }/>
        <div style={{padding:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {recommendedEvents.length===0?(
            <div style={{gridColumn:"span 2",textAlign:"center",padding:"30px 0",color:T.muted,fontSize:12}}>No recommendations available.</div>
          ):recommendedEvents.map((ev,i)=>(
            <div key={i} style={{border:`1px solid ${T.border}`,borderRadius:8,padding:12,background:"#fff",transition:"box-shadow .15s",cursor:"pointer"}}
                 onMouseEnter={e=>e.currentTarget.style.boxShadow="0 6px 20px rgba(10,35,82,.12)"}
                 onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <span style={{fontSize:9,fontWeight:800,color:T.accent,background:T.accent+"18",padding:"2px 7px",borderRadius:99,textTransform:"uppercase",letterSpacing:.4}}>{ev.category}</span>
                <div style={{display:"flex",alignItems:"center",gap:4,background:T.success+"18",borderRadius:99,padding:"2px 7px"}}>
                  <Star size={8} color={T.success} fill={T.success}/>
                  <span style={{fontSize:9,fontWeight:800,color:T.success}}>{ev.match}%</span>
                </div>
              </div>
              <div style={{fontSize:12,fontWeight:800,color:T.navy,marginBottom:4,lineHeight:1.3}}>{ev.name}</div>
              <div style={{fontSize:10,color:T.muted,marginBottom:8}}>{ev.country}</div>
              <div style={{fontSize:10,color:T.danger,fontWeight:700,marginBottom:10,background:"#fee2e2",padding:"3px 7px",borderRadius:5,display:"inline-block"}}>Deadline: {ev.deadline}</div>
              <div style={{display:"flex",gap:6}}>
                <button style={{flex:1,padding:"5px 0",borderRadius:6,border:"none",background:T.orange,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FONT}}>Apply Now</button>
                <button onClick={()=>setSaved(p=>({...p,[ev.id]:!p[ev.id]}))}
                  style={{width:30,height:28,borderRadius:6,border:`1px solid ${T.border}`,background:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Bookmark size={11} color={saved[ev.id]?T.orange:T.muted} fill={saved[ev.id]?T.orange:"none"}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Timeline */}
      <Panel style={{gridColumn:"span 5"}}>
        <PanelHeader icon={Calendar} title="Activity Timeline"/>
        <div style={{padding:"12px 16px",maxHeight:440,overflowY:"auto"}}>
          {timelineData.length===0?(
            <div style={{textAlign:"center",padding:"30px 0",color:T.muted,fontSize:12}}>No timeline activities.</div>
          ):(
            <div style={{position:"relative"}}>
              <div style={{position:"absolute",left:13,top:0,bottom:0,width:1.5,background:T.border}}/>
              {timelineData.map((item,i)=>{
                const colors={application:T.accent,accepted:T.success,invitation:T.purple};
                const c=colors[item.type]||T.navy;
                return (
                  <div key={i} style={{display:"flex",gap:12,marginBottom:14,position:"relative"}}>
                    <div style={{width:27,height:27,borderRadius:"50%",border:`2px solid ${c}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,zIndex:1,background:"#fff"}}>
                      {item.type==="application"?<FileText size={10} color={c}/>:item.type==="accepted"?<CheckCircle size={10} color={c}/>:<Mail size={10} color={c}/>}
                    </div>
                    <div style={{flex:1,paddingTop:4}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <div style={{fontSize:11,fontWeight:700,color:T.navy}}>{item.label}</div>
                          <div style={{fontSize:10,color:T.muted,marginTop:1,lineHeight:1.3}}>{item.detail}</div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                          {item.urgent&&<span style={{fontSize:8,fontWeight:800,color:T.danger,background:"#fee2e2",padding:"1px 5px",borderRadius:99,textTransform:"uppercase"}}>Urgent</span>}
                          <span style={{fontSize:9,color:T.muted,whiteSpace:"nowrap"}}>{item.date}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────
function AnalyticsTab({data}) {
  const {donutRegionApps,donutRegionInv,donutCategory,donutAcceptance,regionData,trendData} = data;
  const donuts=[
    {title:"Apps by Region",data:donutRegionApps},
    {title:"Invites by Region",data:donutRegionInv},
    {title:"By Category",data:donutCategory},
    {title:"Acceptance",data:donutAcceptance},
  ];

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:14}}>
      {/* 4 donuts */}
      {donuts.map((d,i)=>(
        <Panel key={i} style={{gridColumn:"span 3"}}>
          <PanelHeader icon={PieIcon} title={d.title}/>
          <div style={{padding:"8px 0"}}>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={d.data} cx="50%" cy="50%" innerRadius={38} outerRadius={56} paddingAngle={2} dataKey="value">
                  {d.data.map((_,j)=><Cell key={j} fill={DONUT_COLORS[j%DONUT_COLORS.length]}/>)}
                </Pie>
                <Tooltip content={<DonutTooltip/>}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{padding:"0 14px 10px"}}>
              {d.data.slice(0,4).map((item,j)=>{
                const total=d.data.reduce((s,v)=>s+(v.value||0),0)||1;
                return (
                  <div key={j} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                    <div style={{width:7,height:7,borderRadius:2,background:DONUT_COLORS[j%DONUT_COLORS.length],flexShrink:0}}/>
                    <span style={{fontSize:10,color:T.muted,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</span>
                    <span style={{fontSize:10,fontWeight:800,color:T.navy}}>{Math.round((item.value/total)*100)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>
      ))}

      {/* Regional bar */}
      <Panel style={{gridColumn:"span 7"}}>
        <PanelHeader icon={Globe} title="Regional Breakdown"/>
        <div style={{padding:"14px 16px 8px"}}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={regionData} margin={{top:0,right:8,bottom:0,left:-20}}>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="region" tick={{fontSize:10,fill:T.muted}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:10,fill:T.muted}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{borderRadius:8,border:`1px solid ${T.border}`,fontSize:11}}/>
              <Legend iconSize={7} wrapperStyle={{fontSize:11}}/>
              <Bar dataKey="applied"  name="Applied"  fill={T.navy}   radius={[3,3,0,0]}/>
              <Bar dataKey="invited"  name="Invited"  fill={T.purple} radius={[3,3,0,0]}/>
              <Bar dataKey="attended" name="Attended" fill={T.success} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {/* Trend line */}
      <Panel style={{gridColumn:"span 5"}}>
        <PanelHeader icon={TrendingUp} title="Monthly Trends"/>
        <div style={{padding:"14px 16px 8px"}}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData} margin={{top:0,right:8,bottom:0,left:-20}}>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="month" tick={{fontSize:10,fill:T.muted}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:10,fill:T.muted}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{borderRadius:8,border:`1px solid ${T.border}`,fontSize:11}}/>
              <Legend iconSize={7} wrapperStyle={{fontSize:11}}/>
              <Line type="monotone" dataKey="applied"  name="Applied"  stroke={T.navy}   strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="invited"  name="Invited"  stroke={T.purple} strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="accepted" name="Accepted" stroke={T.success} strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
}

// ── Pipeline Tab (Kanban) ─────────────────────────────────────────────────────
function PipelineTab({data}) {
  const {kanbanData} = data;
  const colColors = {Discovered:T.muted,Applied:T.accent,"Under Review":T.warn,Invited:T.purple,Confirmed:T.teal,Completed:T.success};
  const cols = Object.keys(kanbanData);

  return (
    <div style={{display:"flex",gap:12,overflowX:"auto",alignItems:"flex-start",paddingBottom:8}}>
      {cols.map(col=>(
        <div key={col} style={{minWidth:200,flex:"0 0 200px"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,padding:"0 2px"}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:colColors[col]||T.muted}}/>
            <span style={{fontSize:11,fontWeight:800,color:T.navy,textTransform:"uppercase",letterSpacing:.4,flex:1}}>{col}</span>
            <span style={{fontSize:10,background:(colColors[col]||T.muted)+"20",color:colColors[col]||T.muted,borderRadius:99,padding:"1px 7px",fontWeight:800}}>{kanbanData[col].length}</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {kanbanData[col].length===0?(
              <div style={{border:`1.5px dashed ${T.border}`,borderRadius:10,padding:"20px 12px",textAlign:"center",fontSize:10,color:T.muted}}>Empty</div>
            ):kanbanData[col].map((ev,i)=>(
              <div key={i} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 12px",borderTop:`2.5px solid ${colColors[col]||T.muted}`,cursor:"pointer",transition:"box-shadow .15s"}}
                   onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 14px rgba(10,35,82,.12)"}
                   onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                <div style={{fontSize:11,fontWeight:800,color:T.navy,lineHeight:1.3,marginBottom:5}}>{ev.event}</div>
                <div style={{fontSize:10,color:T.muted,marginBottom:4}}>{ev.country}</div>
                <span style={{fontSize:9,fontWeight:800,color:colColors[col]||T.muted,background:(colColors[col]||T.muted)+"18",padding:"1px 6px",borderRadius:99,textTransform:"uppercase",letterSpacing:.3}}>{ev.cat}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Insights Tab ──────────────────────────────────────────────────────────────
function InsightsTab({data}) {
  const {leaderboardCountries,leaderboardSchools,leaderboardCategories,profileMetrics} = data;

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:14}}>
      {/* Countries */}
      <Panel style={{gridColumn:"span 4"}}>
        <PanelHeader icon={Globe} title="Top Countries"/>
        <div style={{padding:"12px 16px"}}>
          {leaderboardCountries.map((c,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:13}}>
              <div style={{width:20,height:20,borderRadius:5,background:`linear-gradient(135deg,${T.navy},${T.navyMid})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:9,fontWeight:900,flexShrink:0}}>{c.rank}</div>
              <span style={{fontSize:17}}>{c.flag}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:3}}>{c.name}</div>
                <ProgressBar value={c.pct} color={c.color} height={4}/>
              </div>
              <span style={{fontSize:12,fontWeight:800,color:c.color,flexShrink:0}}>{c.count}</span>
            </div>
          ))}
        </div>
      </Panel>

      {/* Schools */}
      <Panel style={{gridColumn:"span 4"}}>
        <PanelHeader icon={Building2} title="Top Schools"/>
        <div style={{padding:"12px 16px"}}>
          {leaderboardSchools.map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:13}}>
              <div style={{width:20,height:20,borderRadius:5,background:`linear-gradient(135deg,${T.navy},${T.navyMid})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:9,fontWeight:900,flexShrink:0}}>{s.rank}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:11,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                <div style={{fontSize:10,color:T.muted}}>{s.type}</div>
              </div>
              <div style={{background:s.color+"18",borderRadius:99,padding:"2px 8px",flexShrink:0}}>
                <span style={{fontSize:10,fontWeight:800,color:s.color}}>{s.count} events</span>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Categories */}
      <Panel style={{gridColumn:"span 4"}}>
        <PanelHeader icon={Flag} title="Top Categories"/>
        <div style={{padding:"12px 16px"}}>
          {leaderboardCategories.map((cat,i)=>(
            <div key={i} style={{marginBottom:13}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:7,height:7,borderRadius:2,background:cat.color}}/>
                  <span style={{fontSize:11,fontWeight:700,color:T.text}}>{cat.name}</span>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:cat.color}}>{cat.pct}%</span>
              </div>
              <ProgressBar value={cat.pct*5} color={cat.color} height={5}/>
            </div>
          ))}
        </div>
      </Panel>

      {/* Profile metrics */}
      <Panel style={{gridColumn:"span 12"}}>
        <PanelHeader icon={Award} title="University Profile Performance"/>
        <div style={{padding:"16px 20px",display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:16}}>
          {profileMetrics.map((m,i)=>(
            <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:"16px 12px",border:`1px solid ${T.border}`,borderRadius:10,background:m.color+"05"}}>
              <RadialProgress value={m.value} color={m.color} size={64} strokeWidth={5}/>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,fontWeight:800,color:T.navy,marginBottom:3,lineHeight:1.3}}>{m.label}</div>
                <div style={{fontSize:10,color:T.muted}}>{m.value>=85?"Excellent":m.value>=70?"Good":m.value>=55?"Average":"Needs work"}</div>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export function UniversityEngagementDashboard() {
  const [activeTab, setActiveTab]   = useState("overview");
  const [loading, setLoading]       = useState(true);
  const [tick, setTick]             = useState(0);
  const [dashData, setDashData]     = useState(null);
  const uc = getUserCredentials();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const creds    = Fetchexecutequery();
      const query    = async (sql) => { const res = await axios.post(creds[1],{query:sql},creds[0]); return res.data||[]; };
      const entityId = uc.selectedentity||uc.entities_id;
      const tenantId = uc.tenants_id;

      const [profileRows,eventRows,applicationRows,invitationRows] = await Promise.all([
        query(`SELECT * FROM edu_platform.university_profile WHERE user_id=${uc.user_id} AND is_active=1 ORDER BY primary_id DESC LIMIT 1;`),
        query(`SELECT e.primary_id,e.event_name,e.event_category,e.country,e.city,e.event_from_date,e.event_to_date,e.event_status,e.reg_close_date,e.invitation_release_date,e.acceptance_deadline FROM edu_platform.events e WHERE e.is_active=1 AND e.tenant_id=${tenantId} AND e.event_status IN ('published','registration_open','under_review','completed') ORDER BY e.primary_id DESC;`),
        query(`SELECT ea.primary_id,ea.event_id,ea.application_status,ea.review_status,ea.invitation_status,ea.applied_on,ea.reviewed_on,ea.invited_on,ea.responded_on,e.event_name,e.event_category,e.country,e.city,e.event_from_date,e.event_to_date,up.university_name,up.country AS university_country FROM edu_platform.event_applications ea JOIN edu_platform.events e ON e.primary_id=ea.event_id LEFT JOIN edu_platform.university_profile up ON up.entity_id=ea.applicant_entity_id AND up.is_active=1 WHERE ea.is_active=1 AND ea.applicant_entity_id=${entityId} AND ea.tenant_id=${tenantId} ORDER BY ea.applied_on DESC;`),
        query(`SELECT ei.primary_id,ei.event_id,ei.application_id,ei.invitation_status,ei.invited_on,ei.acceptance_deadline,ei.responded_on,e.event_name,e.event_category,e.country,e.city,e.event_from_date,e.event_to_date,up.university_name,up.country AS university_country FROM edu_platform.event_invitations ei JOIN edu_platform.events e ON e.primary_id=ei.event_id LEFT JOIN edu_platform.university_profile up ON up.entity_id=ei.applicant_entity_id AND up.is_active=1 WHERE ei.is_active=1 AND ei.applicant_entity_id=${entityId} AND ei.tenant_id=${tenantId} ORDER BY ei.invited_on DESC;`),
      ]);

      const profile = profileRows[0]||{university_name:uc.display_name||"University Admin"};
      const applications = applicationRows.map(r=>({...r,school_name:r.university_name||profile.university_name||"Your University",country:r.university_country||r.country||"Unknown"}));
      const invitations  = invitationRows.map(r=>({...r,school_name:r.university_name||profile.university_name||"Your University",country:r.university_country||r.country||"Unknown"}));
      setDashData(buildData({profile,events:eventRows,applications,invitations}));
    } catch (err) {
      console.error("Dashboard load error:",err);
      setDashData(buildData({profile:{university_name:uc.display_name||"University Admin"}}));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(()=>{ loadData(); },[loadData]);

  const activeData = dashData || buildData({profile:{university_name:"University Admin"}});

  const tabComponents = {
    overview:     <OverviewTab     data={activeData}/>,
    applications: <ApplicationsTab data={activeData}/>,
    invitations:  <InvitationsTab  data={activeData}/>,
    events:       <EventsTab       data={activeData}/>,
    analytics:    <AnalyticsTab    data={activeData}/>,
    pipeline:     <PipelineTab     data={activeData}/>,
    insights:     <InsightsTab     data={activeData}/>,
  };

  return (
    <div style={{display:"flex",background:T.bg,fontFamily:FONT,overflow:"hidden"}}>
      {/* ── MAIN AREA ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,overflow:"hidden"}}>
        {/* Content area — scrollable per-tab */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 24px",minHeight:0}}>
          {loading && !dashData ? (
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",flexDirection:"column",gap:16}}>
              <div style={{width:40,height:40,border:`3px solid ${T.border}`,borderTop:`3px solid ${T.navy}`,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
              <div style={{fontSize:13,color:T.muted,fontWeight:600}}>Loading dashboard data…</div>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : tabComponents[activeTab]}
        </div>
      </div>
    </div>
  );
}

export default UniversityEngagementDashboard;